import { createElement } from "react";
import { styled } from "goober";

import { appURLs } from "@/app/constants.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { BUILDING_CATEGORIES } from "@/game-palworld/constants/buildings.mjs";
import { ELEMENTS } from "@/game-palworld/constants/elements.mjs";
import { ITEM_CATEGORIES } from "@/game-palworld/constants/items.mjs";
import { WORK_SUITABILITIES } from "@/game-palworld/constants/work-suitabilities.mjs";
import { GAME_SYMBOL_PALWORLD } from "@/game-palworld/definition-symbol.mjs";
import type {
  ActiveSkill,
  Building,
  Item,
  MapData,
  Pal,
  PassiveSkill,
  Point,
  Point3D,
  Technology,
} from "@/game-palworld/models/model-wiki.mjs";
import overlayRoutes from "@/game-palworld/overlay-routes.mjs";
import routes from "@/game-palworld/routes.mjs";
import GameIconBoxPalworld from "@/inline-assets/game-icon-box-palworld.svg";

export { GAME_SYMBOL_PALWORLD };

const Image = styled("img")`
  height: var(--sp-4);
  width: var(--sp-4);
`;

const definition = {
  gameSymbol: GAME_SYMBOL_PALWORLD,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/PALWORLD-small.svg`,
  iconShape: GameIconBoxPalworld,
  iconBox: GameIconBoxPalworld,
  iconBadge: GameIconBoxPalworld,
  imageHero: "", // todo?
  imageBanner: null, // this one is optional
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/palworld-min.webp`,
  imageWordmark: "", // todo?
  isActive: true,
  fullName: ["common:games.palworld.long", "Palworld"],
  friendlyName: ["common:games.palworld.short", "Palworld"],
  preferFriendlyName: true as const,
  shortName: "palworld" as const,
  backendName: "PALWORLD" as const,
  gameColor: "#1AD1EA",

  navLinks: [
    {
      href: "/palworld/database",
      text: ["common:navigation.database", "Database"],
      menu: [
        [
          {
            category: ["palworld:pals", "Pals"],
            items: [
              {
                text: ["palworld:allPals", "All Pals"],
                href: "/palworld/database/pals",
              },
              {
                href: "/palworld/database/pals?element=Normal",
                text: ["common:navigation.elements", "Elements"],
                menu: {
                  items: Object.entries(ELEMENTS).map(
                    ([key, { label, imageSrc }]) => ({
                      href: `/palworld/database/pals?element=${key}`,
                      text: label,
                      Icon: createElement(Image, { src: imageSrc }),
                    }),
                  ),
                },
              },
              {
                href: "/palworld/database/pals?work=cooling",
                text: ["common:navigation.workSkills", "Work Skills"],
                menu: {
                  items: Object.entries(WORK_SUITABILITIES).map(
                    ([key, { label, src }]) => ({
                      href: `/palworld/database/pals?work=${key}`,
                      text: label,
                      Icon: createElement(Image, { src }),
                    }),
                  ),
                },
              },
            ],
          },
          {
            category: ["palworld:items", "Items"],
            items: [
              {
                text: ["palworld:category.foods", "Foods"],
                href: "/palworld/database/foods",
              },
              {
                text: ["palworld:category.materials", "Materials"],
                href: "/palworld/database/materials",
              },
              {
                text: ["palworld:category.spheres", "Spheres"],
                href: "/palworld/database/spheres",
              },
              {
                text: ["palworld:category.weapons", "Weapons"],
                href: "/palworld/database/weapons",
              },
              {
                text: ["palworld:allCategories", "All Categories"],
                href: "/palworld/database/accessories",
                menu: {
                  items: Object.entries(ITEM_CATEGORIES).map(
                    ([cat, { label }]) => ({
                      text: label,
                      href: `/palworld/database/${cat}`,
                    }),
                  ),
                },
              },
            ],
          },
          {
            category: ["palworld:buildings", "Buildings"],
            items: [
              {
                text: ["palworld:build.category.defense", "Defense"],
                href: "/palworld/database/buildings/defense",
              },
              {
                text: ["palworld:build.category.furniture", "Furniture"],
                href: "/palworld/database/buildings/furniture",
              },
              {
                text: ["palworld:build.category.storage", "Storage"],
                href: "/palworld/database/buildings/storage",
              },
              {
                text: ["palworld:allCategories", "All Categories"],
                href: "/palworld/database/buildings/defense",
                menu: {
                  items: Object.entries(BUILDING_CATEGORIES).map(
                    ([cat, { label }]) => ({
                      text: label,
                      href: `/palworld/database/buildings/${cat}`,
                    }),
                  ),
                },
              },
            ],
          },
          {
            category: ["palworld:technology", "Technology"],
            items: [
              {
                text: ["palworld:allTech", "All Technology"],
                href: "/palworld/database/technology",
              },
              {
                text: ["palworld:buildings", "Buildings"],
                href: "/palworld/database/technology?category=building",
              },
              {
                text: ["palworld:items", "Items"],
                href: "/palworld/database/technology?category=item",
              },
            ],
          },
          {
            category: ["palworld:skills", "Skills"],
            items: [
              {
                text: ["palworld:passiveSkills", "Passive Skills"],
                href: "/palworld/database/passives",
              },
            ],
          },
        ],
      ],
    },
    {
      href: "/palworld/map",
      text: ["palworld:map", "Map"],
    },
    {
      href: "/palworld/breeding-calculator",
      text: ["palworld:breedingCalculator", "Breeding Calculator"],
    },
    {
      href: "/palworld/tier-list",
      text: ["palworld:tierList", "Tier Lists"],
      menu: [
        [
          {
            items: [
              {
                text: ["palworld:tierlist.combat", "Best Combat Pals"],
                href: "/palworld/tier-list/combat",
              },
              {
                text: ["palworld:tierlist.base", "Best Worker Pals"],
                href: "/palworld/tier-list/base",
              },
              {
                text: ["palworld:tierlist.mounts", "Best Mounts"],
                href: "/palworld/tier-list/mount",
              },
            ],
          },
        ],
      ],
    },
    {
      href: `/overlays/palworld`,
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
  ],
  settings: () => import("@/game-palworld/Settings.jsx"),
  IPC: () => import("@/game-palworld/init.mjs"),
  routes,
  overlayRoutes,
  overlays: {
    mapMarker: {
      key: ["isMapMarkerEnabled"],
      title: ["overlays:palworld.mapMarker.title", "Map marker"],
      description: [
        "overlays:palworld.mapMarker.description",
        "See Pals and their stats. Mark their spawn locations to capture them all!",
      ],
      tags: ["utility"],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/palworldOverlay/map-marker-large.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/palworldOverlay/map-marker-small.webp`,
      },
      date: new Date(2024, 2, 2),
    },
  },
  overlaySettings: {
    isMapMarkerEnabled: true,
    mapMarkers: {
      show_players: false,
      show_travels: false,
      show_relics: true,
      show_treasures: false,
      show_eggs: false,
      show_stones: false,
      show_metals: false,
      show_paladiums: false,
    },
  },
  meta: {
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/palworld.webp`,
    overlays: {
      title: ["overlays:meta.palworld.title", "Palworld Overlays and Tools"],
      description: [
        "overlays:meta.palworld.description",
        "Palworld map marker overlay and much more",
      ],
      subtitle: [
        "overlays:meta.palworld.subtitle",
        "All the guides you need to make your Palworld experience much more enjoyable.",
      ],
    },
    guides: [
      () => import("@/game-palworld/components/HomeBreedingCalculatorTile.jsx"),
      () => import("@/game-palworld/components/HomeTierListsTile.jsx"),
    ],
    articles: [
      {
        createdAt: new Date("Feb 21, 2024 15:00:00"),
        title: ["welcome:palworld.feature.database", "Palworld Database"],
        subtitle: [
          "welcome:palworld.feature.databaseDesc",
          "Find everything you would want to know about Palworld.",
        ],
        link: {
          href: `/palworld/database`,
          text: ["welcome:palworld.feature.map.cta", "View Database"],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-database-bg_1.webp`,
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-database-img_1.webp`,
      },
      {
        createdAt: new Date("Feb 21, 2024 15:00:00"),
        title: ["welcome:palworld.feature.map", "Map"],
        subtitle: [
          "welcome:palworld.feature.mapDesc",
          "Explore Palworld map with all locations, spawn locations, Pals, chests, and more.",
        ],
        link: {
          href: `/palworld/map`,
          text: ["welcome:palworld.feature.map.cta", "View Map"],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-map-bg_1.webp`,
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-map-img_1.webp`,
      },
      {
        createdAt: new Date("Feb 21, 2024 15:00:00"),
        title: ["welcome:palworld.feature.search", "Map Search Overlay"],
        subtitle: [
          "welcome:palworld.feature.searchDesc",
          "Search for items you need and see where they are located on the map.",
        ],
        link: {
          href: `/overlays/palworld`,
          text: ["common:navigation.viewStatistics", "View Statistics"],
        },
        background: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-search-bg_1.webp`,
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/card-search-img_1.webp`,
      },
    ],
    features: [
      {
        title: ["palworld.features.database.title", "Pals Database"],
        description: [
          "palworld.features.database.description",
          "Learn every crucial detail of Pals and Items.",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/database_1.webp`,
        link: {
          href: "/palworld/database",
        },
      },
      {
        title: ["palworld.features.map.title", "Map"],
        description: [
          "palworld.features.map.description",
          "Search for items you need and see where they are located on the map.",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/map_1.webp`,
        link: {
          href: "/palworld/map",
        },
      },
      {
        title: [
          "palworld.features.breedingCalculator.title",
          "Breeding Calculator",
        ],
        description: [
          "palworld.features.breedingCalculator.description",
          "Predict Pal outcomes to breed the strongest.",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/calc_1.webp`,
        link: {
          href: "/palworld/breeding-calculator",
        },
      },
      {
        title: ["palworld.features.tierLists.title", "Tier Lists"],
        description: [
          "palworld.features.tierLists.description",
          "Learn what Pals have the most potential.",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/tierlist_1.webp`,
        link: {
          href: "/palworld/tier-list/combat",
        },
      },
      {
        title: [
          "palworld.features.mapSearchOverlay.title",
          "Map Search Overlay",
        ],
        description: [
          "palworld.features.mapSearchOverlay.description",
          "Search for and keep track of elusive items and Pals.",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/palworld/overlay_1.webp`,
        link: {
          href: "/overlays/palworld",
        },
      },
    ],
  },
} satisfies GameDefinition<typeof GAME_SYMBOL_PALWORLD>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    palworld: {
      wiki: {
        activeSkills: Record<string, Record<string, ActiveSkill>>;
        buildings: Record<string, Record<string, Building>>;
        buildingTech: Record<string, Record<string, Technology>>;
        items: Record<string, Record<string, Item>>;
        itemTech: Record<string, Record<string, Technology>>;
        mapData: Record<string, MapData>;
        pals: Record<string, Record<string, Pal>>;
        /// [both, day, night]
        palSpawns: Record<string, Record<string, [Point[], Point[], Point[]]>>;
        passives: Record<string, Record<string, PassiveSkill>>;
        /// [both, day, night]
        spawnLocations: Record<
          string,
          Record<string, [Point3D[], Point3D[], Point3D[]]>
        >;
        i18n: Record<string, Record<string, string>>;
      };
    };
  }
}

export default definition;
